import React from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  Mail, 
  Search, 
  Zap, 
  Calendar,
  CheckCircle,
  Linkedin,
  MessageSquare,
  ArrowRight,
  BarChart,
  Target,
  Settings
} from 'lucide-react';
import MouseFollowEffect from '../components/MouseFollowEffect';

export default function PopupTalent() {
  return (
    <div className="page-container pt-20 min-h-screen">
      <div className="content-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="relative">
              <Users className="w-16 h-16 text-golden animate-pulse" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Welcome, <span className="text-golden">Zina</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Thank you for taking the time to discuss how we can help Popup Talent grow through our lead generation system. Your questions helped us better understand your needs and goals. We touched on many points during our meeting, so we've distilled the key information below.
          </p>
        </motion.div>

        {/* System Overview */}
        <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-8 mb-12 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-golden">System Overview</h2>
          <p className="text-gray-300 mb-6">
            Our advanced system delivers up to <span className="text-golden font-semibold">2,000 personalized outreach emails daily</span>. Each communication is individually crafted to resonate with the recipient, resulting in exceptional response rates and engagement.
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
              <h3 className="text-xl font-bold mb-4">Technology Stack</h3>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <Linkedin className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-300">LinkedIn Sales Navigator for precise prospect identification</span>
                </li>
                <li className="flex items-start">
                  <Zap className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-300">Make.com for seamless workflow automation</span>
                </li>
                <li className="flex items-start">
                  <Mail className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-300">Instantly.ai for optimized email campaign delivery</span>
                </li>
                <li className="flex items-start">
                  <Search className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span className="text-gray-300">Any Mail Finder for accurate contact acquisition</span>
                </li>
              </ul>
            </div>

            <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
              <h3 className="text-xl font-bold mb-4">Performance Metrics</h3>
              <div className="mb-6">
                <img 
                  src="/Images/Screenshot 2025-03-07 at 17.49.34.png" 
                  alt="Campaign Results" 
                  className="w-full rounded-xl"
                />
              </div>
              <p className="text-gray-300">
                Our system consistently achieves a <span className="text-golden font-bold">57.1% positive response rate</span>, significantly outperforming industry standards.
              </p>
            </div>
          </div>
        </MouseFollowEffect>

        {/* Lead Quality & Tracking */}
        <div className="mb-12">
          <div className="bg-black/20 border border-gray-800 rounded-2xl p-8 max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 text-golden text-center">Quality Assurance</h2>
            <p className="text-gray-300 mb-8 text-center">
              Leveraging your exceptional closing capabilities, we implement rigorous lead qualification processes to ensure only the highest-quality opportunities reach your pipeline.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-black/40 p-4 rounded-xl border border-gray-700">
                <Target className="w-10 h-10 text-golden mb-3" />
                <h3 className="font-bold mb-2">Pre-Qualification</h3>
                <p className="text-gray-300 text-sm">
                  Comprehensive prospect evaluation before engagement
                </p>
              </div>
              <div className="bg-black/40 p-4 rounded-xl border border-gray-700">
                <BarChart className="w-10 h-10 text-golden mb-3" />
                <h3 className="font-bold mb-2">Lead Tracking</h3>
                <p className="text-gray-300 text-sm">
                  End-to-end pipeline visibility and analytics
                </p>
              </div>
              <div className="bg-black/40 p-4 rounded-xl border border-gray-700">
                <Settings className="w-10 h-10 text-golden mb-3" />
                <h3 className="font-bold mb-2">Quality Control</h3>
                <p className="text-gray-300 text-sm">
                  Multi-stage verification process
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-golden/20 to-black/30 p-6 rounded-xl border border-golden/30">
              <h3 className="text-xl font-bold mb-4 text-center">Our Commitment</h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-3 mt-1" />
                  <p className="text-gray-300">Thorough prospect qualification prior to meeting scheduling</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-3 mt-1" />
                  <p className="text-gray-300">Comprehensive attribution tracking for all generated leads</p>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-3 mt-1" />
                  <p className="text-gray-300">Minimum of 4 qualified meetings per week once fully operational</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Investment Section */}
        <div className="mb-16">
          <div className="bg-gradient-to-r from-golden/20 to-black/30 rounded-2xl p-8 border border-golden/30 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-center">Investment Structure</h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                <h3 className="text-xl font-bold mb-4 text-golden">Implementation Fee</h3>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white mb-2">$1,300</p>
                  <p className="text-gray-300">One-time setup fee (valid for 60 days after agreement)</p>
                </div>
              </div>
              
              <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                <h3 className="text-xl font-bold mb-4 text-golden">Performance Fee</h3>
                <div className="text-center">
                  <p className="text-4xl font-bold text-white mb-2">$200-350</p>
                  <p className="text-gray-300">Per qualified meeting (final amount to be determined in second meeting)</p>
                </div>
              </div>
            </div>

            <div className="text-center">
              <p className="text-xl text-gray-300 mb-4">
                Our performance-based pricing model ensures alignment with your objectives. Rather than requiring a long-term commitment, we operate on a per-meeting basis, providing maximum flexibility and ROI focus.
              </p>
              
              <p className="text-gray-300 mb-8">
                This structure allows you to concentrate on your core strength - closing deals - while we manage the entire lead generation process.
              </p>
              
              <a
                href="https://cal.com/dsmarketing.meeting.with.pascoal/lead-gen-setup-call"
                className="inline-flex items-center px-8 py-4 bg-golden text-white rounded-full text-lg font-semibold hover:bg-golden-600 transition-all duration-300"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Schedule Implementation Call <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}